/* eslint-disable camelcase */
import { useFormik } from 'formik';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { ButtonGroup, Col, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from "react-router-dom";
import * as Yup from 'yup';
import formatDate from '../../../../../../app/frontend/components/components/utils/formatDate';
import Price from '../../../../../frontend/components/components/utils/numbers/Price';
import { setOffer } from '../../../../actions/offer.actions';
import Accordion from '../../../../shared/components/Accordion';
import Button from '../../../../shared/components/Button';
import Checkbox from '../../../../shared/components/Checkbox/Checkbox';
import axios from '../../../../utils/axiosConfig';
import { INFLUENCERS_ENDPOINTS } from '../../../../config/api';
import { OFFER_STATUSES } from '../../../../utils/constants';
import { addFlashMessage } from '../../../../actions/flashMessages.actions';
import { signOut } from '../../../../actions/auth.actions';

const validationSchema = Yup.object().shape({
  influencer_services_acknowledge: Yup.boolean().required().oneOf([true]),
});

export const hasFilledTermsAndLegals = (offer) => {
  const requirements = get(offer, 'required_acknowledgements') || {};

  return offer.user_offer &&
    Object.keys(requirements)
      .filter(key => requirements[key])
      .every(key => offer.user_offer[key]);
};

const TermsAndLegal = ({
  onRequestClose,
  nextStep,
  lastStep
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const offer = useSelector(store => get(store, 'offer.offer'));
  const applying = get(offer, 'user_offer.status') === OFFER_STATUSES.APPLYING;
  const userOffer = get(offer, 'user_offer') || {};
  const history = useHistory();
  const lumanuEnabled = get(offer, 'lumanu_enabled');
  const isOptionalPosting = get(offer, 'is_optional_posting');
  const testFlow = useSelector(store => get(store, 'config.test'));
  const isFrench = (i18n.language || '').toLowerCase().startsWith('fr');
  const participationTermsLink = isFrench
    ? <a href="/influencers/terms-of-service"
      target="_blank" rel="noopener noreferrer" key="terms-of-service" />
    : <Link to="/terms-of-service" target="_blank" key="terms-of-service" />;

  const [state, setState] = useState({
    loading: false,
    timestamp: 0,
    status: undefined
  });

  const handleLogout = useCallback(() => {
    axios.delete('/api/v1/influencers/users/sign_out')
      .then(({ data }) => {
        signOut(dispatch);
        history.push('/signin');
        window.location.reload(true);
      }).catch(() => {
        dispatch(addFlashMessage({
          text: t('influencers.common.message_error'),
          type: 'error'
        }));
      });
  }, [history, signOut]);

  async function validateChecked(v = false) {
    return Yup.boolean().required().oneOf([true]).validate(v)
      .then(() => null).catch(() => t('influencers.common.forms.is_required_to_be_checked'));
  }

  const formik = useFormik({
    initialValues: {
      payment_acknowledge: get(userOffer, 'payment_acknowledge') || false,
      total_deliverables_acknowledge: get(userOffer, 'total_deliverables_acknowledge') || false,
      posting_deadline_acknowledge: get(userOffer, 'posting_deadline_acknowledge') || false,
      influencer_services_acknowledge: get(userOffer, 'influencer_services_acknowledge') || false,
      paid_amplification_acknowledge: get(userOffer, 'paid_amplification_acknowledge') || false,
      content_rights_acknowledge: get(userOffer, 'content_rights_acknowledge') || false,
      exclusivity_acknowledge: get(userOffer, 'exclusivity_acknowledge') || false
    },
    validationSchema,
    onSubmit: values => {
      // if (testFlow.some(t => ['fb_required', 'fb_optional', 'yt'].includes(t)))
      //   return nextStep();

      // If already completed this step, just go to next
      if (hasFilledTermsAndLegals(offer)) return nextStep();
      onSaveInfo(values, true);
    },
  });

  // Not all acknowledges are required, depends on different conditionals,
  // here we are extracting those required
  useEffect(() => {
    const newValues = {};
    const newTouched = {};

    if (get(offer, 'payments.length')) {
      formik.registerField('payment_acknowledge', { validate: validateChecked });
      newValues.payment_acknowledge = get(userOffer, 'payment_acknowledge') || false;
      newTouched.payment_acknowledge = true;
    }

    if (get(offer, 'timelines.posting_window.end')) {
      formik.registerField('posting_deadline_acknowledge', { validate: validateChecked });
      newValues.posting_deadline_acknowledge = get(userOffer, 'posting_deadline_acknowledge') || false;
      newTouched.posting_deadline_acknowledge = true;
    }

    if (
      get(offer, 'deliverables') &&
      Object.values(offer.deliverables).filter(v => v > 0).length
    ) {
      formik.registerField('total_deliverables_acknowledge', { validate: validateChecked });
      newValues.total_deliverables_acknowledge = get(userOffer, 'total_deliverables_acknowledge') || false;
      newTouched.total_deliverables_acknowledge = true;
    }

    if (
      get(offer, 'creative_brief.paid_amplification_include') === 'on'
      && get(offer, 'creative_brief.paid_amplification')
    ) {
      formik.registerField('paid_amplification_acknowledge', { validate: validateChecked });
      newValues.paid_amplification_acknowledge = get(userOffer, 'paid_amplification_acknowledge') || false;
      newTouched.paid_amplification_acknowledge = true;
    }

    if (get(offer, 'full_media_rights_instructions')) {
      formik.registerField('content_rights_acknowledge', { validate: validateChecked });
      newValues.content_rights_acknowledge = get(userOffer, 'content_rights_acknowledge') || false;
      newTouched.content_rights_acknowledge = true;
    }

    if (
      get(offer, 'creative_brief.exclusivity_include') === 'on'
      && get(offer, 'creative_brief.exclusivity')
    ) {
      formik.registerField('exclusivity_acknowledge', { validate: validateChecked });
      newValues.exclusivity_acknowledge = get(userOffer, 'exclusivity_acknowledge') || false;
      newTouched.exclusivity_acknowledge = true;
    }

    formik.registerField('influencer_services_acknowledge', { validate: validateChecked });
    newValues.influencer_services_acknowledge = get(userOffer, 'influencer_services_acknowledge') || false;
    newTouched.influencer_services_acknowledge = true;

    formik.setValues({ ...newValues })
      .then(() => setTimeout(() => {
        formik.setTouched(newTouched);
      }, 400));
  }, []);

  const onSaveInfo = (values, submit = false) => {
    setState({
      ...state,
      loading: true
    });
    let user_offer = {
      ...values,
      ...(!submit && { skip_validation: true })
    };

    const hasExistingUserOffer = !!get(offer, 'user_offer.id');
    const request = hasExistingUserOffer
      ? axios.patch(INFLUENCERS_ENDPOINTS.OFFERS.USER_OFFERS.PATCH(offer.user_offer.id), { user_offer })
      : axios.post(INFLUENCERS_ENDPOINTS.OFFERS.USER_OFFERS.POST(offer.id), { user_offer });

    request.then(({ data }) => {
      const offerTmp = {
        ...offer,
        user_offer: { ...data.user_offer }
      };
      dispatch(setOffer(offerTmp));

      if (submit && hasFilledTermsAndLegals(offerTmp)) {
        nextStep({ offer: offerTmp });
      }

      setState({
        ...state,
        loading: false,
        timestamp: Date.now()
      });
    }).catch(err => {
      setState({
        ...state,
        loading: false,
        error: err.response.data.error,
        status: get(err.response, 'status')
      });
    });
  };

  function parsePayment(payment_data) {
    const htmlDiv = document.createElement('div');
    htmlDiv.innerHTML = payment_data;
    return htmlDiv.textContent || htmlDiv.innerText || "";
  }

  const sumPayments = useCallback((payments) => {
    if (!payments.length) return 0;
    return payments.reduce((total, payment) => total += payment.user_payment_value || 0, 0);
  }, []);

  const renderPayments = useCallback(() => {
    if (!offer.payments || !offer.payments.length) return null;

    const paymentSuffix = t('influencers.pages.campaign.apply_side_pane.payment_amount_suffix', { defaultValue: '' });

    return <InputGroup className="mb-3">
      <Checkbox
        name="payment_acknowledge"
        onChange={formik.handleChange}
        value={formik.values.payment_acknowledge}
        defaultStatus={get(userOffer, 'payment_acknowledge') ? "checked" : "unchecked"}
        disabled={get(userOffer, 'payment_acknowledge')}
        label={
          <span className="text-1">
            {offer.incentives.payment_include == "on" ?
              <div
                dangerouslySetInnerHTML={{
                  __html: parsePayment(offer.incentives.payment)
                }}>
              </div>
              : <div> {`${t('influencers.pages.campaign.apply_side_pane.payment_will_be')} `}
                <Price number={sumPayments(offer.payments)} currency={get(offer, 'payments[0].currency')} />
                {paymentSuffix ? ` ${paymentSuffix}` : ''}
              </div>
            }
          </span>
        }
      />
    </InputGroup >;
  }, [offer.payments, state.timestamp]);

  const renderDeliverables = useCallback(() => {
    if (!offer.deliverables || !Object.keys(offer.deliverables).length) return null;

    const deliverables = [];
    Object.keys(offer.deliverables)
      .filter(k => k !== 'other')
      .forEach(key => {
        const amount = offer.deliverables[key];
        const name = t(`influencers.common.posts.${key}`, {
          count: amount
        });
        if (!amount || !name) return null;
        deliverables.push(`${amount} ${name}`);
      });

    if (get(offer, 'deliverables.other')) {
      deliverables.push(`${offer.deliverables.other} ${offer.other_deliverable_label}`);
    }

    if (!deliverables.length) return null;

    return <InputGroup className="mb-3">
      <Checkbox name="total_deliverables_acknowledge"
        onChange={formik.handleChange}
        value={formik.values.total_deliverables_acknowledge}
        defaultStatus={get(userOffer, 'total_deliverables_acknowledge') ? "checked" : "unchecked"}
        disabled={get(userOffer, 'total_deliverables_acknowledge')}
        label={
          <span className="text-1">
            {t('influencers.pages.campaign.apply_side_pane.total_deliverables')}{' '}{deliverables.join(', ')}
          </span>
        }
      />
    </InputGroup>;
  }, [offer.deliverables, state.timestamp]);

  const renderPostingWindow = useCallback(() => {
    if (!offer.timelines || !offer.timelines.posting_window || !offer.timelines.posting_window.end) return null;

    return (
      <InputGroup className="mb-3">
        <Checkbox
          name="posting_deadline_acknowledge"
          onChange={formik.handleChange}
          value={formik.values.posting_deadline_acknowledge}
          defaultStatus={get(userOffer, 'posting_deadline_acknowledge') ? "checked" : "unchecked"}
          disabled={get(userOffer, 'posting_deadline_acknowledge')}
          label={
            <span className="text-1">
              {t('influencers.pages.campaign.apply_side_pane.all_content')}{' '}
              {formatDate(offer.timelines.posting_window.end)}
            </span>
          }
        />
      </InputGroup>
    );
  }, [offer.timelines, state.timestamp]);

  const renderContentRights = useCallback(() => {
    if (!offer.is_media_rights_enabled && !offer.show_content_rights_expiration_instructions) return null;

    return (<React.Fragment>
      <hr className='my-6' />
      <h4 className="mb-4">
        {t('influencers.pages.campaign.glance.content_rights.title')}
      </h4>

      { offer.show_content_rights_expiration_instructions ?
          <Accordion className="mb-3"
            collapsibles={[{
              header: t('common.terms_conditions'),
              content: <div
                dangerouslySetInnerHTML={{
                  __html: offer.content_rights_expiration_instructions
                }}></div>
            }]} /> :
          <Accordion className="mb-3"
            collapsibles={[{
              header: t('common.terms_conditions'),
              content: <div
                dangerouslySetInnerHTML={{
                  __html: offer.hide_media_rights_price ? offer.media_rights_influencer_instructions : offer.full_media_rights_instructions
                }}></div>
            }]} />
      }

      <InputGroup className="mb-3">
        <Checkbox
          name="content_rights_acknowledge"
          onChange={formik.handleChange}
          value={formik.values.content_rights_acknowledge}
          defaultStatus={get(userOffer, 'content_rights_acknowledge') ? "checked" : "unchecked"}
          disabled={get(userOffer, 'content_rights_acknowledge')}
          label={
            <span className='text-1'>
              {t('influencers.pages.campaign.reviewed_terms_conditions')}
            </span>
          }
        />
      </InputGroup>
    </React.Fragment>);
  }, [offer.full_media_rights_instructions, state.timestamp]);

  const renderPaidAmplification = useCallback(() => {
    if (!offer.creative_brief
      || offer.creative_brief.paid_amplification_include !== "on"
      || !offer.creative_brief.paid_amplification) return null;

    return (<React.Fragment>
      <hr className='my-6' />
      <h4 className="mb-4">
        {t('influencers.pages.campaign.paid_amplification')}
      </h4>

      <Accordion className="mb-3"
        defaultActiveIndex={1}
        collapsibles={[{
          header: t('common.terms_conditions'),
          content: <div
            dangerouslySetInnerHTML={{
              __html: offer.creative_brief.paid_amplification
            }}></div>
        }]} />
      <InputGroup className="mb-3">
        <Checkbox
          name="paid_amplification_acknowledge"
          onChange={formik.handleChange}
          value={formik.values.paid_amplification_acknowledge}
          disabled={get(userOffer, 'paid_amplification_acknowledge')}
          label={
            <span className='text-1'>
              {t('influencers.pages.campaign.reviewed_terms_conditions_paid_amp')}
            </span>
          }
        />
      </InputGroup>
    </React.Fragment>);
  }, [offer.creative_brief, state.timestamp]);

  const renderExclusivity = useCallback(() => {
    if (!offer.creative_brief
      || offer.creative_brief.exclusivity_include !== "on"
      || !offer.creative_brief.exclusivity) return null;

    return (<React.Fragment>
      <hr className='my-6' />
      <h4 className="mb-4">
        {t('influencers.pages.campaign.exclusivity')}
      </h4>

      <Accordion className="mb-3"
        defaultActiveIndex={1}
        collapsibles={[{
          header: t('common.terms_conditions'),
          content: <div
            dangerouslySetInnerHTML={{
              __html: offer.creative_brief.exclusivity
            }}></div>
        }]} />
      <InputGroup className="mb-3">
        <Checkbox
          name="exclusivity_acknowledge"
          onChange={formik.handleChange}
          value={formik.values.exclusivity_acknowledge}
          defaultStatus={get(userOffer, 'exclusivity_acknowledge') ? "checked" : "unchecked"}
          disabled={get(userOffer, 'exclusivity_acknowledge')}
          label={
            <span className='text-1'>
              {t('influencers.pages.campaign.reviewed_terms_conditions_exclusivity')}
            </span>
          }
        />
      </InputGroup>
    </React.Fragment>);
  }, [offer.creative_brief, state.timestamp]);

  return (
    <React.Fragment>
      <h3 className='mb-4'>
        {t('influencers.pages.campaign.apply_side_pane.terms_and_legal')}
      </h3>

      <h4 className='mb-4'>
        {t('influencers.pages.campaign.apply_side_pane.acknowledgment')}
      </h4>

      <div className="text-1 mb-4">
        {t('influencers.pages.campaign.apply_side_pane.check_acknowledgment')}
      </div>
      <Form onSubmit={formik.handleSubmit}>

        {renderPayments()}
        {lumanuEnabled && <p className="small gray">{t('influencers.pages.campaign.apply_side_pane.lumanu_payment_acknowledgment')}</p>}
        {isOptionalPosting && <p className="small gray">{t('influencers.pages.campaign.glance.optional_posting')}</p>}
        {renderDeliverables()}
        {renderPostingWindow()}

        <InputGroup className="mb-3">
          <Checkbox
            name="influencer_services_acknowledge"
            onChange={formik.handleChange}
            value={formik.values.influencer_services_acknowledge}
            disabled={get(userOffer, 'influencer_services_acknowledge')}
            label={
              <span className="text-1">
                <Trans i18nKey='influencers.pages.campaign.apply_side_pane.participation_governed'
                  components={[participationTermsLink]}>
                </Trans>
              </span>
            }
          />
        </InputGroup>

        {renderContentRights()}
        {renderPaidAmplification()}
        {renderExclusivity()}
        {get(offer, 'show_seeding_cancel_warning', false) &&
         <p style={{color: 'red'}}>
           {t('influencers.pages.campaign.apply_side_pane.cancelation_warning')}
         </p>}

        <hr className='my-6' />
        <Row className='justify-content-end'>
          <Col className="col-auto">
            <ButtonGroup className="non-adaptable">
              <Button
                variant="tertiary"
                type="button"
                onClick={() => onRequestClose()}
                disabled={state.loading}>
                {t('influencers.common.cancel')}
              </Button>
              {/*
               <Button
                variant="secondary"
                type="button"
                onClick={() => onSaveInfo(formik.values)}
                disabled={state.loading || hasFilledTermsAndLegals(offer)}>
                {t('influencers.common.save')}
              </Button>
              */}
              <Button
                idButton="offer-application-terms-legal"
                lastStep={lastStep()}
                variant="primary"
                type="submit"
                disabled={state.loading || !formik.isValid}>
                {state.loading
                  ? <Spinner animation="border" size="sm" />
                  : t('influencers.common.next')
                }
              </Button>
            </ButtonGroup>
            <Form.Text className="text-muted danger-text text-end">
              {state.error}
              {state.status == 401 &&
                <Button
                  variant="link"
                  type="button"
                  onClick={handleLogout}>
                  {t('influencers.header.sign_out')}
                </Button>
              }
            </Form.Text>
          </Col>
        </Row>
      </Form>
    </React.Fragment>
  );
};

TermsAndLegal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  lastStep: PropTypes.func.isRequired
};

export default React.memo(TermsAndLegal);
